import React from 'react'
import styled from 'styled-components'
import useShop from '../context/AppContext'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { Layout } from '../layout'
import { images } from '../constants'
import { motion } from 'framer-motion'
import { QuoteForm } from '../components'

const SectionWrapper = styled.div`
  display: flex;
  overflow: hidden;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 100%;
  min-height: calc(100dvh - 100px);
`
const SectionBackground = styled.div`
  position: absolute;
  z-index: 0;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 1200px;
  background-repeat: no-repeat;
  background-position: center bottom;
  background-size: 100% auto;
  background-image: url(${images.quote_bottom});
  @media screen and (max-width: 1023px) {
    height: 800px;
    background-size: 2000px auto;
  }
  @media screen and (max-width: 800px) {
    height: 500px;
    background-size: 1600px auto;
  }
`
const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  z-index: 1;
  width: 86%;
  max-width: var(--max-width);
  padding: 50px 0;
  margin: 0 auto;
  h1 {
    line-height: 1.25;
    margin-bottom: 15px;
  }
  h3 {
    font-size: var(--fz-lg);
    color: var(--text);
    line-height: 1.5;
    font-weight: 400;
    margin-bottom: 30px;
  }
  @media screen and (max-width: 800px) {
    width: calc(100% - 50px);
    margin: 0 25px;
  }
`
const FooterWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  height: auto;
  padding-bottom: 120px;
  background-color: var(--black-dark);
  @media screen and (max-width: 1023px) {
    padding-bottom: 100px;
  }
  @media screen and (max-width: 800px) {
    padding-bottom: 80px;
  }
`
const FooterContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 86%;
  max-width: var(--max-width);
  margin: 0 auto;
  p {
    margin-bottom: 15px;
    color: var(--text);
    font-size: var(--fz-md);
  }
  @media screen and (max-width: 800px) {
    flex-direction: column;
    width: calc(100% - 50px);
    margin: 0 25px;
  }
`
const LeftColumn = styled.div`
  width: 50%;
  padding: 40px 20px 0 30px;
  .shopify-icon {
    opacity. 0.9;
    height: 32px;
    margin-bottom: 20px;
  }
  @media screen and (max-width: 1023px) {
    width: 50%;
    padding: 40px 20px 0 20px;
  }
  @media screen and (max-width: 800px) {
    width: 100%;
    padding: 40px 0 0 0;
  }
`
const RightColumn = styled.div`
  width: 50%;
  padding: 92px 30px 0 20px;
  @media screen and (max-width: 1023px) {
    width: 50%;
    padding: 92px 20px 0 20px;
  }
  @media screen and (max-width: 800px) {
    width: 100%;
    padding: 0;
  }
`
const SignatureContainer = styled.div`
  display: flex;
  flex-direction: row;
  .profile-image {
    overflow: hidden;
    width: 56px;
    height: 56px;
    margin-right: 15px;
    border-radius: 50%;
  }
`

const Quote = () => {
  const { preloaderExecuted, navigationExecuted } = useShop()

  const title = <h1>Let's start here!</h1>
  const subtitle = <h3>As a Shopify e-commerce expert, I've got all your development and design needs covered, and I'll contact you within one business day to clarify or confirm your request.</h3>
  const shopify = <img src={images.service_shopify} alt="Shopify" className="shopify-icon" />

  const servicesLink = (
    <Link to="/shopify-services" className="secondary-link">
      Shopify services
    </Link>
  )

  const contactLink = (
    <Link to="/contact" className="secondary-link">
      get in touch
    </Link>
  )

  return (
    <Layout>
      <Helmet>
        <title>Get a Free Quote – Robert Eberhard</title>
      </Helmet>
      <SectionWrapper>
        <SectionBackground />
        {preloaderExecuted && navigationExecuted ? (
          <SectionContainer>
            <motion.div animate={{ y: [15, 0], opacity: [0, 1] }} transition={{ delay: 0.1, duration: 0.3 }} className="start-opacity">
              {title}
            </motion.div>
            <motion.div animate={{ y: [15, 0], opacity: [0, 1] }} transition={{ delay: 0.2, duration: 0.3 }} className="start-opacity">
              {subtitle}
            </motion.div>
            <motion.div animate={{ y: [15, 0], opacity: [0, 1] }} transition={{ delay: 0.3, duration: 0.3 }} className="start-opacity">
              <QuoteForm />
            </motion.div>
          </SectionContainer>
        ) : (
          <SectionContainer>
            <motion.div animate={{ y: [15, 0], opacity: [0, 1] }} transition={{ delay: 1, duration: 0.3 }} className="start-opacity">
              {title}
            </motion.div>
            <motion.div animate={{ y: [15, 0], opacity: [0, 1] }} transition={{ delay: 1.1, duration: 0.3 }} className="start-opacity">
              {subtitle}
            </motion.div>
            <motion.div animate={{ y: [15, 0], opacity: [0, 1] }} transition={{ delay: 1.2, duration: 0.3 }} className="start-opacity">
              <QuoteForm />
            </motion.div>
          </SectionContainer>
        )}
      </SectionWrapper>
      <FooterWrapper>
        <FooterContainer>
          <LeftColumn>
            {shopify}
            <p>I offer a range of {servicesLink} to help you create and manage your online store, and provide ongoing support and maintenance to keep you ahead of the competition.</p>
          </LeftColumn>
          <RightColumn>
            <p>Don't hesitate to {contactLink} with me. I look forward to connecting with you and finding out how I can help you achieve your digital goals.</p>
            <SignatureContainer>
              <div>
                <img className="profile-image" src={images.profile} alt="Profile image" />
              </div>
              <div>
                <p>
                  Keep Rocking ✌️ <br></br>Robert Eberhard
                </p>
              </div>
            </SignatureContainer>
          </RightColumn>
        </FooterContainer>
      </FooterWrapper>
    </Layout>
  )
}

export default Quote
